import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Input,
  Button,
  notification,
  Table,
  Tag,
  Divider,
  Spin,
  Popconfirm,
  Space,
  Tabs,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import {
  MaturityPaymentRequest,
  PaymentResponse,
} from '../../model/Payment.model';
import PaymentService from '../../service/payment-service/Payment.service';
import {
  getTotalAmount,
  formatCurrency as utilsFormatCurrency,
} from '../maturity-schedule/MaturityTableUtils';
import useEmployee from '../../hook/useEmployee';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

interface MaturityPaymentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  maturitySchedule: MaturityScheduleResponse | null;
}

const MaturityPaymentModal: React.FC<MaturityPaymentModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  maturitySchedule,
}) => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<PaymentResponse[]>([]);
  const [deletedPayments, setDeletedPayments] = useState<PaymentResponse[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [deletedPaymentsLoading, setDeletedPaymentsLoading] = useState(false);
  const [deletedPaymentsError, setDeletedPaymentsError] = useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = useState('active');
  const [editingPayment, setEditingPayment] = useState<PaymentResponse | null>(
    null
  );
  const [editModalVisible, setEditModalVisible] = useState(false);
  const { employee } = useEmployee();

  // Load existing payments when modal opens
  useEffect(() => {
    if (visible && maturitySchedule) {
      loadPayments();
    }
  }, [visible, maturitySchedule]);

  const loadPayments = async () => {
    if (!maturitySchedule) return;

    try {
      setPaymentsLoading(true);
      const paymentsData = await PaymentService.getPaymentsByMaturitySchedule(
        maturitySchedule.id
      );
      setPayments(paymentsData);
    } catch (error) {
      console.error('Error loading payments:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách thanh toán',
      });
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadDeletedPayments = async () => {
    if (!maturitySchedule) return;

    try {
      setDeletedPaymentsLoading(true);
      setDeletedPaymentsError(null);
      const response =
        await PaymentService.getDeletedPaymentsByMaturitySchedule(
          maturitySchedule.id
        );
      if (response.success && response.data) {
        setDeletedPayments(response.data);
      } else {
        setDeletedPaymentsError(
          response.message || 'Không thể tải danh sách thanh toán đã xóa'
        );
      }
    } catch (error) {
      console.error('Error loading deleted payments:', error);
      setDeletedPaymentsError(
        'Có lỗi xảy ra khi tải danh sách thanh toán đã xóa'
      );
      setDeletedPayments([]);
    } finally {
      setDeletedPaymentsLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'deleted') {
      loadDeletedPayments(); // Always fetch when switching to deleted tab
    } else if (key === 'active') {
      loadPayments(); // Always fetch when switching to active tab
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (!maturitySchedule) {
        notification.error({
          message: 'Lỗi',
          description: 'Không tìm thấy thông tin kỳ đáo hạn',
        });
        return;
      }

      if (!employee) {
        notification.error({
          message: 'Lỗi',
          description: 'Không tìm thấy thông tin nhân viên đang đăng nhập',
        });
        return;
      }

      const paymentData: MaturityPaymentRequest = {
        amount: values.amount,
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        notes: values.notes || '',
      };

      await PaymentService.createMaturityPayment(
        maturitySchedule.id,
        paymentData,
        employee.id
      );

      notification.success({
        message: 'Thành công',
        description: 'Đã ghi nhận thanh toán cho kỳ đáo hạn',
      });

      form.resetFields();
      await loadPayments();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating payment:', error);
      notification.error({
        message: 'Lỗi',
        description:
          error.response?.data || 'Có lỗi xảy ra khi ghi nhận thanh toán',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPayment = (payment: PaymentResponse) => {
    setEditingPayment(payment);
    editForm.setFieldsValue({
      amount: payment.amount,
      paymentDate: dayjs(payment.paymentDate),
      notes: '', // Payment model doesn't have notes field
    });
    setEditModalVisible(true);
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !employee) return;

    try {
      setLoading(true);
      const values = await editForm.validateFields();

      const updateData = {
        amount: values.amount,
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        notes: values.notes || '',
      };

      await PaymentService.updatePayment(
        editingPayment.id,
        updateData,
        employee.id
      );

      notification.success({
        message: 'Thành công',
        description: 'Đã cập nhật thông tin thanh toán',
      });

      setEditModalVisible(false);
      setEditingPayment(null);
      editForm.resetFields();
      await loadPayments();
      onSuccess();
    } catch (error: any) {
      console.error('Error updating payment:', error);
      notification.error({
        message: 'Lỗi',
        description:
          error.response?.data?.message ||
          'Có lỗi xảy ra khi cập nhật thanh toán',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    try {
      setLoading(true);
      await PaymentService.deletePayment(paymentId, employee?.id as number);

      notification.success({
        message: 'Thành công',
        description: 'Đã xóa thanh toán và hoàn tiền',
      });

      await loadPayments();
      // If on deleted tab, refresh it too
      if (activeTab === 'deleted') {
        loadDeletedPayments();
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      notification.error({
        message: 'Lỗi',
        description:
          error.response?.data?.message || 'Có lỗi xảy ra khi xóa thanh toán',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setPayments([]);
    setDeletedPayments([]);
    setActiveTab('active');
    setDeletedPaymentsError(null);
    onCancel();
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingPayment(null);
    editForm.resetFields();
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'PENDING':
        return 'orange';
      default:
        return 'default';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Hoàn thành';
      case 'PENDING':
        return 'Đang chờ';
      default:
        return status;
    }
  };

  // Table columns for active payments
  const paymentColumns: ColumnsType<PaymentResponse> = [
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Ngày thanh toán',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'originalStatus',
      key: 'originalStatus',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: 'Nhân viên ghi nhận',
      dataIndex: 'employee',
      key: 'employee',
      width: 120,
      render: (employee: any, record: PaymentResponse) => {
        return employee?.name || record.employee?.name || 'N/A';
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY HH:mm'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      render: (_, record: PaymentResponse) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditPayment(record)}
            title="Sửa thanh toán"
          />
          <Popconfirm
            title="Xác nhận xóa"
            description="Bạn có chắc chắn muốn xóa thanh toán này? Tiền sẽ được hoàn lại."
            onConfirm={() => handleDeletePayment(record.id)}
            okText="Xóa"
            cancelText="Hủy"
            okType="danger"
          >
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              danger
              title="Xóa thanh toán"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Table columns for deleted payments (no actions)
  const deletedPaymentColumns: ColumnsType<PaymentResponse> = [
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Ngày thanh toán',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'originalStatus',
      key: 'originalStatus',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)} (Đã xóa)
        </Tag>
      ),
    },
    {
      title: 'Nhân viên ghi nhận',
      dataIndex: 'employee',
      key: 'employee',
      width: 120,
      render: (employee: any, record: PaymentResponse) => {
        return employee?.name || record.employee?.name || 'N/A';
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY HH:mm'),
    },
    {
      title: 'Người xóa',
      dataIndex: 'deletedByName',
      key: 'deletedByName',
      width: 140,
      render: (deletedByName: string | null) => deletedByName || '-',
    },
    {
      title: 'Ngày xóa',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY HH:mm'),
    },
  ];

  // Calculate total amount (thành tiền) using the same formula as in the table
  const totalAmount = maturitySchedule ? getTotalAmount(maturitySchedule) : 0;

  // Calculate total payment amount
  const totalPaymentAmount = payments
    .filter(
      (payment) => payment.originalStatus === 'COMPLETED' && !payment.deletedAt
    )
    .reduce((total, payment) => total + payment.amount, 0);

  // Calculate remaining amount = totalAmount - totalPaymentAmount
  const remainingAmount = totalAmount - totalPaymentAmount;

  return (
    <>
      <Modal
        title={<span className="text-sm">Thanh toán kỳ đáo hạn</span>}
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} size="small">
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
            size="small"
          >
            Ghi nhận
          </Button>,
        ]}
        width={900}
        destroyOnClose
      >
        {maturitySchedule && (
          <div className="mb-3 p-2 bg-gray-50 rounded">
            <h4 className="font-semibold mb-1 text-xs">Thông tin kỳ đáo hạn</h4>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <span className="text-gray-600">Thẻ:</span>
                <div className="font-medium">
                  {maturitySchedule.creditCard?.cardNumber
                    ? `***${maturitySchedule.creditCard.cardNumber.slice(-4)}`
                    : 'N/A'}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Số tiền:</span>
                <div className="font-medium text-blue-600">
                  {formatCurrency(maturitySchedule.repaymentAmount)}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Thành tiền:</span>
                <div className="font-medium text-purple-600">
                  {formatCurrency(totalAmount)}
                </div>
              </div>
            </div>
            {totalPaymentAmount > 0 && (
              <div className="mt-2 pt-1 border-t text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Đã TT:</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(totalPaymentAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Còn lại:</span>
                  <span className="font-semibold text-orange-600">
                    {formatCurrency(remainingAmount)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payments with Tabs */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-2">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="flex-1"
              size="small"
            >
              <TabPane tab="Hiện tại" key="active" />
              <TabPane tab="Đã xóa" key="deleted" />
            </Tabs>
            <h4 className="text-xs font-semibold text-gray-700 ml-2">
              Lịch sử TT
            </h4>
          </div>

          <div>
            {activeTab === 'active' && (
              <Spin spinning={paymentsLoading}>
                <Table
                  columns={paymentColumns}
                  dataSource={payments}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  locale={{
                    emptyText: 'Chưa có thanh toán nào',
                  }}
                  scroll={{ x: 800 }}
                />
              </Spin>
            )}

            {activeTab === 'deleted' && (
              <>
                <div className="mb-3 flex justify-end">
                  <Button
                    type="text"
                    size="small"
                    onClick={loadDeletedPayments}
                    loading={deletedPaymentsLoading}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Làm mới
                  </Button>
                </div>

                {deletedPaymentsError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                    <div className="font-medium">Lỗi tải dữ liệu:</div>
                    <div>{deletedPaymentsError}</div>
                  </div>
                )}

                <Spin spinning={deletedPaymentsLoading}>
                  <Table
                    columns={deletedPaymentColumns}
                    dataSource={deletedPayments}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    locale={{
                      emptyText: !deletedPaymentsError ? (
                        <div className="text-center py-8">
                          <div className="text-gray-400 mb-2">
                            Không có thanh toán đã xóa
                          </div>
                          <div className="text-xs text-gray-500">
                            Các thanh toán đã xóa sẽ hiển thị tại đây
                          </div>
                        </div>
                      ) : null,
                    }}
                    scroll={{ x: 800 }}
                  />
                </Spin>
              </>
            )}
          </div>
        </div>

        <Divider />

        <h4 className="font-semibold mb-4">Ghi nhận thanh toán mới</h4>

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            paymentDate: dayjs(),
            amount: 0,
          }}
        >
          <Form.Item
            name="amount"
            label="Số tiền thanh toán"
            rules={[
              { required: true, message: 'Vui lòng nhập số tiền' },
              { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              placeholder="Nhập số tiền thanh toán"
              addonAfter="VND"
            />
          </Form.Item>

          <Form.Item
            name="paymentDate"
            label="Ngày thanh toán"
            rules={[
              { required: true, message: 'Vui lòng chọn ngày thanh toán' },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Chọn ngày thanh toán"
            />
          </Form.Item>

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={3} placeholder="Nhập ghi chú (tùy chọn)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Payment Modal */}
      <Modal
        title="Sửa thông tin thanh toán"
        open={editModalVisible}
        onCancel={handleEditCancel}
        footer={[
          <Button key="cancel" onClick={handleEditCancel}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleUpdatePayment}
          >
            Cập nhật
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Số tiền thanh toán"
            rules={[
              { required: true, message: 'Vui lòng nhập số tiền' },
              { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              placeholder="Nhập số tiền thanh toán"
              addonAfter="VND"
            />
          </Form.Item>

          <Form.Item
            name="paymentDate"
            label="Ngày thanh toán"
            rules={[
              { required: true, message: 'Vui lòng chọn ngày thanh toán' },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Chọn ngày thanh toán"
            />
          </Form.Item>

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={3} placeholder="Nhập ghi chú (tùy chọn)" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MaturityPaymentModal;
